import type { z } from './zod';

export type HttpMethod = 'get' | 'post' | 'patch' | 'put' | 'delete';

/**
 * How a tool behaves for agents (docs/01-architectuur/05-api-en-mcp.md, schrijftools):
 * read (direct), write (direct, idempotency key), destructive (dry-run + confirmToken),
 * external (goes outside or touches money: creates an Approval).
 */
export type ActionKind = 'read' | 'write' | 'destructive' | 'external';

export interface ActionDef<I extends z.ZodTypeAny, O extends z.ZodTypeAny> {
  /** Tool name, namespace.resource.action, e.g. time.entry.create. */
  name: string;
  method: HttpMethod;
  /** Path under /api/v1 with {param} placeholders, e.g. /time/entries/{id}. */
  path: string;
  /** Exactly one permission module:action, or 'authenticated' for actions every signed-in caller may use. */
  permission: string;
  input: I;
  output: O;
  successStatus?: 200 | 201 | 202 | 204;
  tags?: string[];
  summary?: string;
  description?: string;
  kind?: ActionKind;
  /** GET on one object returns an ETag; PATCH/DELETE require If-Match (REST) or accept expectedVersion (MCP). */
  etag?: boolean;
  /** Not exposed as MCP tool (only for the exception list: portal, binary downloads). */
  restOnly?: boolean;
  /** The answer carries a secret, such as a token: it is never kept for an Idempotency-Key replay (CO-0038). */
  secret?: boolean;
}

export interface Action<I extends z.ZodTypeAny = z.ZodTypeAny, O extends z.ZodTypeAny = z.ZodTypeAny> {
  name: string;
  method: HttpMethod;
  path: string;
  permission: string;
  input: I;
  output: O;
  successStatus: 200 | 201 | 202 | 204;
  tags: string[];
  summary: string;
  description?: string;
  kind: ActionKind;
  etag: boolean;
  restOnly: boolean;
  secret: boolean;
  /** Names of the {param} placeholders in the path. */
  pathParams: string[];
  /** Builds the concrete path plus query string, for the web client. */
  pathFor(input: z.input<I>): string;
}

const namePattern = /^[a-z][a-zA-Z0-9]*\.[a-z][a-zA-Z0-9]*\.[a-z][a-zA-Z0-9]*$/;

/** One action, one schema: the source for the REST route, the MCP tool and the web client types (ADR-0016). */
export function defineAction<I extends z.ZodTypeAny, O extends z.ZodTypeAny>(
  def: ActionDef<I, O>,
): Action<I, O> {
  if (!namePattern.test(def.name)) throw new Error(`Action name ${def.name} must be namespace.resource.action`);
  const pathParams = [...def.path.matchAll(/\{([a-zA-Z0-9]+)\}/g)].map((m) => m[1]!);
  const kind = def.kind ?? (def.method === 'get' ? 'read' : 'write');
  return {
    ...def,
    successStatus: def.successStatus ?? (def.method === 'post' ? 201 : 200),
    tags: def.tags ?? [def.name.split('.')[0]!],
    summary: def.summary ?? def.name,
    kind,
    etag: def.etag ?? false,
    restOnly: def.restOnly ?? false,
    secret: def.secret ?? false,
    pathParams,
    pathFor(input) {
      const values = { ...(input as Record<string, unknown>) };
      let path = def.path;
      for (const p of pathParams) {
        path = path.replace(`{${p}}`, encodeURIComponent(String(values[p])));
        delete values[p];
      }
      if (def.method === 'get' || def.method === 'delete') {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(values)) {
          if (v === undefined || v === null) continue;
          qs.set(k, Array.isArray(v) ? v.join(',') : String(v));
        }
        const s = qs.toString();
        if (s) path += `?${s}`;
      }
      return path;
    },
  };
}

/** True when the input goes in the query string (GET, DELETE) instead of a JSON body. */
export const usesQuery = (a: Pick<Action, 'method'>) => a.method === 'get' || a.method === 'delete';
